<template>
    <div class="scenic-detail-container">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>
      
      <div v-else-if="spot" class="spot-detail">
        <div class="spot-header">
          <h1>{{ spot.name }}</h1>
          <div class="spot-meta">
            <span class="rating">
              <span class="stars">★★★★★</span>
              <span>{{ spot.rating }}</span>
            </span>
            <span class="price">¥{{ spot.price }}</span>
          </div>
        </div>
        
        <div class="spot-gallery">
          <img :src="spot.mainImage" :alt="spot.name" class="main-image" />
          <div class="thumbnail-gallery">
            <img 
              v-for="(image, index) in spot.images" 
              :key="index" 
              :src="image" 
              :alt="`${spot.name} - 图片 ${index + 1}`"
              @click="spot.mainImage = image"
              class="thumbnail"
            />
          </div>
        </div>
        
        <div class="spot-info">
          <div class="info-section">
            <h2>景点介绍</h2>
            <p>{{ spot.description }}</p>
          </div>
          
          <div class="info-section">
            <h2>开放时间</h2>
            <p>{{ spot.openingHours }}</p>
          </div>
          
          <div class="info-section">
            <h2>地址</h2>
            <p>{{ spot.address }}</p>
          </div>
          
          <div class="info-section">
            <h2>特色亮点</h2>
            <ul class="highlights">
              <li v-for="(highlight, index) in spot.highlights" :key="index">
                {{ highlight }}
              </li>
            </ul>
          </div>
        </div>
        
        <div class="spot-tips">
          <h2>游玩贴士</h2>
          <ul>
            <li v-for="(tip, index) in spot.tips" :key="index">{{ tip }}</li>
          </ul>
        </div>
      </div>
      
      <div v-else class="error-message">
        <p>抱歉，未找到该景点信息</p>
        <button @click="goBack" class="back-button">返回上一页</button>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted} from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  
  const route = useRoute();
  const router = useRouter();
  const loading = ref(true);
  const spot = ref(null);
  
  // 初始数据 - 平遥古城
  const scenicSpots = [
    {
        id: 'pingyao',
        name: '平遥古城',
        rating: 4.8,
        price: 125,
        mainImage: '/images/景点图片文件夹/平遥古城图片集/平遥古城1.jpg',
        images: [
            '/images/景点图片文件夹/平遥古城图片集/平遥古城1.jpg',
            '/images/景点图片文件夹/平遥古城图片集/平遥古城2.jpg',
            '/images/景点图片文件夹/平遥古城图片集/平遥古城3.jpg',
            '/images/景点图片文件夹/平遥古城图片集/平遥古城4.jpg'
        ],
        description: '平遥古城是中国保存最为完整的四大古城之一，也是世界文化遗产。始建于西周宣王时期（公元前827年—公元前782年），明洪武三年（1370年）扩建，历史上曾是清代晋商的发源地。平遥古城是中国汉民族城市在明清时期的杰出范例，其完整地保存了明清时期的城墙、街道、店铺和民居，展示了中国古代城市的文化、社会、经济和宗教发展的非凡历史见证。',
        openingHours: '08:00-19:00（旺季），08:00-18:00（淡季）',
        address: '山西省晋中市平遥县古城内',
        highlights: [
            '完整的明清古城墙',
            '日升昌票号 - 中国第一家票号',
            '明清一条街',
            '县衙、城隍庙等古建筑群',
            '传统晋商民居'
        ],
        tips: [
            '建议购买通票，可参观古城内主要景点',
            '最佳游览时间为4 - 5小时',
            '可在古城内住一晚，体验古城夜景',
            '古城内有许多传统小吃，如平遥牛肉、碗托等',
            '春秋两季是最佳旅游季节'
        ]
    },
    {
        id: 'yungang',
        name: '云冈石窟',
        rating: 4.7,
        price: 120,
        mainImage: '/images/景点图片文件夹/云岗石窟图片集/云冈石窟1.png',
        images: [
            '/images/景点图片文件夹/云岗石窟图片集/云冈石窟1.png',
            '/images/景点图片文件夹/云岗石窟图片集/云冈石窟2.png',
            '/images/景点图片文件夹/云岗石窟图片集/云冈石窟3.png',
            '/images/景点图片文件夹/云岗石窟图片集/云冈石窟4.png'
        ],
        description: '云冈石窟是中国四大石窟之一，距今已有1500余年历史。它始建于北魏时期，由北魏皇室主持开凿，石窟依山开凿，东西绵延约1公里，存有主要洞窟45个，大小窟龛252个，石雕造像51000余躯，形象地记录了印度及中亚佛教艺术向中国佛教艺术发展的历史轨迹，反映出佛教造像在中国逐渐世俗化、民族化的过程，是中西文化融合的艺术典范。',
        openingHours: '08:30-17:00',
        address: '山西省大同市云冈区云冈镇',
        highlights: [
            '昙曜五窟，气魄宏大，雕饰瑰丽',
            '大佛窟，有高达13.7米的露天大佛',
            '石窟中的飞天、供养人等雕刻，线条优美',
            '丰富多样的佛教故事雕刻壁画'
        ],
        tips: [
            '建议请导游讲解，更深入了解石窟文化',
            '游览时长3 - 4小时',
            '冬季石窟内温度较低，注意保暖',
            '周边有许多特色的北魏风格纪念品可购买'
        ]
    },
    {
        id: 'wutaishan',
        name: '五台山',
        rating: 4.6,
        price: 135,
        mainImage: '/images/景点图片文件夹/五台山图片集/五台山1.png',
        images: [
            '/images/景点图片文件夹/五台山图片集/五台山1.png',
            '/images/景点图片文件夹/五台山图片集/五台山2.png',
            '/images/景点图片文件夹/五台山图片集/五台山3.png',
            '/images/景点图片文件夹/五台山图片集/五台山4.png'
        ],
        description: '五台山是中国四大佛教名山之首，文殊菩萨的道场。它由五座台顶组成，分别为东台望海峰、西台挂月峰、南台锦绣峰、北台叶斗峰、中台翠岩峰，五峰之外称台外，五峰之内称台内，台内以台怀镇为中心。五台山寺庙林立，保存有大量的古建筑、佛像和佛教文化遗产，是中国佛教文化的重要圣地。',
        openingHours: '全天开放',
        address: '山西省忻州市五台县境内',
        highlights: [
            '五爷庙，许愿灵验，香火旺盛',
            '显通寺，历史悠久，建筑宏伟',
            '菩萨顶，是五台山最大的喇嘛寺院',
            '黛螺顶，可徒步攀登“大智路”，体验佛教修行之路'
        ],
        tips: [
            '入寺参观要尊重宗教礼仪，着装得体',
            '夏季山上气温多变，记得携带外套',
            '可在山上住宿体验佛教文化氛围',
            '五台山的斋饭值得一试'
        ]
    },
    {
        id: 'yingxian',
        name: '应县木塔',
        rating: 4.5,
        price: 60,
        mainImage: '/images/景点图片文件夹/应县木塔图片集/应县木塔1.png',
        images: [
            '/images/景点图片文件夹/应县木塔图片集/应县木塔1.png',
            '/images/景点图片文件夹/应县木塔图片集/应县木塔2.png',
            '/images/景点图片文件夹/应县木塔图片集/应县木塔3.png',
            '/images/景点图片文件夹/应县木塔图片集/应县木塔4.png'
        ],
        description: '应县木塔即佛宫寺释迦塔，是世界上现存最古老、最高大的纯木结构楼阁式建筑。木塔建于辽清宁二年（1056年），金明昌六年（1195年）增修完毕。木塔高67.31米，底层直径30.27米，呈平面八角形，全塔耗材红松木料3000立方米，2600多吨，纯木结构、无钉无铆，历经多次地震仍屹立不倒，是中国古代建筑艺术的瑰宝。',
        openingHours: '08:00-18:00',
        address: '山西省朔州市应县城内西北佛宫寺内',
        highlights: [
            '独特的榫卯结构，体现古代建筑技艺的高超',
            '塔内供奉的两颗佛牙舍利',
            '从塔上可俯瞰应县县城风貌'
        ],
        tips: [
            '参观时注意保护文物，不要触摸木塔构件',
            '周边有应县特色的凉粉等美食',
            '建议游览时间1 - 2小时'
        ]
    },
    {
        id: 'jinci',
        name: '晋祠',
        rating: 4.6,
        price: 80,
        mainImage: '/images/景点图片文件夹/晋祠图片集/晋祠1.png',
        images: [
            '/images/景点图片文件夹/晋祠图片集/晋祠1.png',
            '/images/景点图片文件夹/晋祠图片集/晋祠2.png',
            '/images/景点图片文件夹/晋祠图片集/晋祠3.png',
            '/images/景点图片文件夹/晋祠图片集/晋祠4.png'
        ],
        description: '晋祠是为纪念晋国开国诸侯唐叔虞（后被追封为晋王）及母后邑姜后而建，是中国现存最早的皇家祭祀园林，也是三晋历史文脉的综合载体。晋祠内有几十座古建筑，具有中华传统文化特色，其中难老泉、侍女像、圣母像被誉为“晋祠三绝”，展现了中国古代建筑、雕塑、园林等多方面的艺术成就。',
        openingHours: '08:30-17:00',
        address: '山西省太原市晋源区晋祠镇',
        highlights: [
            '圣母殿，宋代建筑的代表，气势恢宏',
            '鱼沼飞梁，十字形桥梁，造型独特',
            '周柏唐槐，千年古树，见证历史沧桑'
        ],
        tips: [
            '最好在春季或秋季游览，景色宜人',
            '景区内有讲解服务，可更好了解历史文化',
            '附近有许多农家乐，可品尝当地农家菜'
        ]
    },
    {
        id: 'qiaojiadayuan',
        name: '乔家大院',
        rating: 4.7,
        price: 115,
        mainImage: '/images/景点图片文件夹/乔家大院/乔家大院1.png',
        images: [
            '/images/景点图片文件夹/乔家大院图片集/乔家大院1.png',
            '/images/景点图片文件夹/乔家大院图片集/乔家大院2.png',
            '/images/景点图片文件夹/乔家大院图片集/乔家大院3.png',
            '/images/景点图片文件夹/乔家大院图片集/乔家大院4.png'
        ],
        description: '乔家大院是清代著名商业金融资本家乔致庸的宅第，建筑宏伟壮观，布局严谨，设计精巧，全院呈双“喜”字形，分为6个大院，内套20个小院，313间房屋。乔家大院集中体现了清代北方民居的独特风格，是研究晋商文化和建筑艺术的重要实物资料。',
        openingHours: '08:30-18:30',
        address: '山西省祁县乔家堡村',
        highlights: [
            '精美的砖雕、木雕、石雕艺术，工艺精湛',
            '乔家大院内展示的晋商文化和历史资料',
            '各院落的建筑布局和装饰风格'
        ],
        tips: [
            '避开旅游高峰期，游览体验更佳',
            '周边有许多售卖晋商特色纪念品的店铺',
            '游览时间建议2 - 3小时'
        ]
    },
    {
        id: 'hengshan',
        name: '恒山',
        rating: 4.5,
        price: 55,
        mainImage: '/images/景点图片文件夹/恒山图片集/恒山1.png',
        images: [
            '/images/景点图片文件夹/恒山图片集/恒山1.png',
            '/images/景点图片文件夹/恒山图片集/恒山2.png',
            '/images/景点图片文件夹/恒山图片集/恒山3.png',
            '/images/景点图片文件夹/恒山图片集/恒山4.png'
        ],
        description: '恒山是中国五岳之一，号称“人天北柱”“绝塞名山”。恒山山脉陡峭，沟谷深邃，自古就是兵家必争之地。山上怪石争奇，古树参天，苍松翠柏之间散布着楼台殿宇，以奇松、怪石、幽洞、清泉等景观而闻名。恒山还是道教圣地，有着丰富的道教文化底蕴。',
        openingHours: '08:00-18:00',
        address: '山西省大同市浑源县城南4公里处',
        highlights: [
            '悬空寺，如临深渊的险峻建筑，巧夺天工',
            '恒山庙，历史悠久，建筑风格独特',
            '登上主峰天峰岭，可俯瞰壮丽的山川景色'
        ],
        tips: [
            '登山时注意安全，穿舒适的鞋子',
            '冬季山上寒冷，做好保暖措施',
            '可乘坐缆车上下山，节省体力'
        ]
    },
    {
        id: 'wangjiadayuan',
        name: '王家大院',
        rating: 4.7,
        price: 110,
        mainImage: '/images/景点图片文件夹/王家大院图片集/王家大院1.png',
        images: [
            '/images/景点图片文件夹/王家大院图片集/王家大院1.png',
            '/images/景点图片文件夹/王家大院图片集/王家大院2.png',
            '/images/景点图片文件夹/王家大院图片集/王家大院3.png',
            '/images/景点图片文件夹/王家大院图片集/王家大院4.png'
        ],
        description: '王家大院是清代民居建筑的集大成者，由静升王氏家族经明清两朝、历300余年修建而成，包括五巷六堡一条街，总面积达25万平方米。大院建筑规模宏大，气势磅礴，融合了木雕、砖雕、石雕等多种艺术形式，展示了晋商的辉煌和明清民居建筑的高超技艺。',
        openingHours: '08:00-18:00',
        address: '山西省灵石县城东12公里处的静升镇',
        highlights: [
            '红门堡建筑群，布局呈“王”字造型',
            '孝义祠，体现传统的孝义文化',
            '各种精美的雕刻装饰，题材丰富'
        ],
        tips: [
            '游览时可跟随导游了解建筑背后的故事',
            '周边有许多当地特色的手工艺品可购买',
            '建议游览时间3 - 4小时'
        ]
    },
    {
        id:'mianshan',
        name: '绵山',
        rating: 4.6,
        price: 110,
        mainImage: '/images/景点图片文件夹/绵山图片集/绵山1.png',
        images: [
            '/images/景点图片文件夹/绵山图片集/绵山1.png',
            '/images/景点图片文件夹/绵山图片集/绵山2.png',
            '/images/景点图片文件夹/绵山图片集/绵山3.png',
            '/images/景点图片文件夹/绵山图片集/绵山4.png'
        ],
        description: '绵山地处汾河之阴，因春秋时期晋国介子推携母隐居被焚于此而著称，是中国清明节（寒食节）的发源地。绵山自然风光秀丽，有诸多奇特的自然景观，同时也有丰富的历史文化遗迹和宗教建筑，如抱腹岩、云峰寺等，集自然景观与人文景观于一体。',
        openingHours: '08:00-19:00',
        address: '山西省介休市绵山镇',
        highlights: [
            '抱腹岩，抱二百余间殿宇、馆舍于“腹”内，甚为壮观',
            '大罗宫，依山而建，气势恢宏，是道教文化的展示地',
            '水涛沟，自然风光优美，瀑布成群'
        ],
        tips: [
            '景区较大，建议乘坐景区观光车游览',
            '夏季是游览绵山的好时节，可避暑纳凉',
            '景区内有特色的素斋可供品尝'
        ]
    }
];
  
  // 根据路由参数获取景点信息
  const fetchSpotData = () => {
    loading.value = true;
    const spotId = route.params.id;
    
    // 模拟API请求延迟
    setTimeout(() => {
      // 在实际应用中，这里应该是一个API请求
      spot.value = scenicSpots.find(s => s.id === spotId) || null;
      loading.value = false;
    }, 800);
  };
  
  // 返回上一页
  const goBack = () => {
    router.back();
  };
  
  onMounted(() => {
    fetchSpotData();
  });
  </script>
  
  <style scoped>
  .scenic-detail-container {
    margin-top: 80px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  }
  
  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top-color: #42b983;
    animation: spin 1s ease-in-out infinite;
    margin-bottom: 10px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .spot-header {
    margin-bottom: 20px;
  }
  
  .spot-header h1 {
    font-size: 28px;
    margin-bottom: 10px;
    color: #333;
  }
  
  .spot-meta {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  
  .rating {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .stars {
    color: #ffcc00;
    letter-spacing: -2px;
  }
  
  .price {
    font-size: 18px;
    font-weight: bold;
    color: #e74c3c;
  }
  
  .spot-gallery {
    margin-bottom: 30px;
  }
  
  .main-image {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 10px;
  }
  
  .thumbnail-gallery {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 10px;
  }
  
  .thumbnail {
    width: 100px;
    height: 70px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    transition: opacity 0.2s;
  }
  
  .thumbnail:hover {
    opacity: 0.8;
  }
  
  .spot-info {
    margin-bottom: 30px;
  }
  
  .info-section {
    margin-bottom: 20px;
  }
  
  .info-section h2 {
    font-size: 20px;
    margin-bottom: 10px;
    color: #333;
    border-left: 4px solid #42b983;
    padding-left: 10px;
  }
  
  .highlights {
    list-style-type: disc;
    padding-left: 20px;
  }
  
  .highlights li {
    margin-bottom: 5px;
  }
  
  .spot-tips {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
  }
  
  .spot-tips h2 {
    font-size: 20px;
    margin-bottom: 15px;
    color: #333;
  }
  
  .spot-tips ul {
    list-style-type: none;
    padding: 0;
  }
  
  .spot-tips li {
    position: relative;
    padding-left: 20px;
    margin-bottom: 10px;
  }
  
  .spot-tips li:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #42b983;
  }
  
  .error-message {
    text-align: center;
    padding: 50px 0;
  }
  
  .back-button {
    background-color: #42b983;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 15px;
    transition: background-color 0.3s;
  }
  
  .back-button:hover {
    background-color: #3aa876;
  }
  
  @media (max-width: 768px) {
    .main-image {
      height: 250px;
    }
    
    .spot-header h1 {
      font-size: 24px;
    }
    
    .thumbnail {
      width: 80px;
      height: 60px;
    }
  }
  </style>